import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PartyPopper, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { API, ROLE_BADGE_STYLES } from "@/constants/roles";
import { MeltoLogo } from "@/components/MeltoLogo";

export default function InviteSetup() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(err.response?.data?.detail || "This invite link is invalid."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/invites/${token}/accept`, { password });
      localStorage.setItem("melto_token", data.token);
      toast.success("Welcome to the crew — your worker account is live.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Setup failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fbfbfd] px-4">
      <div data-testid="invite-setup-page" className="w-full max-w-md rounded-3xl border border-slate-200/80 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10">
        <div className="flex items-center gap-2.5">
          <MeltoLogo size={40} testid="invite-logo-mark" />
          <div>
            <p className="font-display text-base font-bold text-slate-900">Melto Studios</p>
            <p className="font-mono-label text-[9px] font-bold uppercase tracking-widest text-cyan-600">Crew invite</p>
          </div>
        </div>

        {error ? (
          <div className="mt-8 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
            <h1 data-testid="invite-error" className="mt-4 font-display text-xl font-bold text-slate-900">Invite unavailable</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{error}</p>
            <Link to="/login" className="mt-6 inline-block rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-cyan-600">
              Go to login
            </Link>
          </div>
        ) : !invite ? (
          <p data-testid="invite-loading" className="flex items-center justify-center gap-2 py-16 text-sm font-medium text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-cyan-500" /> Loading your invite...
          </p>
        ) : (
          <>
            <div className="mt-8">
              <div className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-cyan-500" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">You're in, {invite.name.split(" ")[0]}.</h1>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                Melto has invited you to join the crew as{" "}
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[invite.role] || ROLE_BADGE_STYLES["General / Multiple Roles"]}`}>
                  {invite.role}
                </span>
              </p>
              {invite.pay_rate && (
                <p className="mt-2 text-sm text-slate-500">
                  Agreed pay rate: <span className="font-bold text-slate-900">{invite.pay_rate}</span>
                </p>
              )}
              <p className="mt-2 text-xs text-slate-400">Account: {invite.email}</p>
            </div>

            <form data-testid="invite-setup-form" onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="invite-password" className="mb-1.5 block text-xs font-semibold text-slate-700">Create your password</label>
                <div className="relative">
                  <input
                    id="invite-password"
                    data-testid="invite-password-input"
                    type={showPw ? "text" : "password"}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className={`${inputCls} pr-12`}
                  />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password visibility">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="invite-confirm" className="mb-1.5 block text-xs font-semibold text-slate-700">Confirm password</label>
                <input
                  id="invite-confirm"
                  data-testid="invite-confirm-input"
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Same password again"
                  className={inputCls}
                />
              </div>
              <button
                data-testid="invite-accept-btn"
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-all hover:bg-cyan-600 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Activate my worker account
              </button>
              <p className="text-center text-xs text-slate-400">You'll land straight in your worker dashboard after this.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
